import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchBids } from '../actions/bidsActions';
import { subscribeToOrdersChanged } from '@bidding/shared/helpers/ordersChangedSignal';

// Bidding writers are synthetic — the backend stamps every bid with a
// deterministic `reveal_at` at order-create time, so the client drips
// them from a single fetch. No polling, no SSE, no Mercure needed
// for the bids list (Mercure stays for chat messages via ChatDock).
//
// Refetch triggers:
//   · mount / uniqueNumber change
//   · tab becomes visible     — catches offline period, manual tab swap
//   · window focus            — same family, covers "alt-tab back" too
//   · cross-tab storage event — another tab hired / cancelled / posted
//                               an order and broadcast the change so
//                               this tab's bid list doesn't stay stale
// Safe default shape — `state.bids` is undefined in bundles that don't
// register `bidsReducer` (e.g., the `account` SPA; see `app.js`). The
// `|| EMPTY` fallback keeps destructuring from blowing up when the hook
// is called from a shared component (Header → FinishOrderBanner) that
// mounts on SPAs without the bidding-flow reducer wired up.
const EMPTY_BIDS_STATE = Object.freeze({});

export default function useBids(uniqueNumber) {
    const dispatch = useDispatch();
    const { list, order, mercure, loading, error } = useSelector(
        state => state.bids || EMPTY_BIDS_STATE,
    );

    useEffect(() => {
        if (!uniqueNumber) return undefined;
        dispatch(fetchBids(uniqueNumber));

        const refetchIfVisible = () => {
            if (document.visibilityState === 'visible') {
                dispatch(fetchBids(uniqueNumber));
            }
        };
        document.addEventListener('visibilitychange', refetchIfVisible);
        window.addEventListener('focus', refetchIfVisible);

        // Cross-tab: some other tab changed order state (hire / cancel /
        // create). Refetch immediately so this tab's UI reflects the
        // new reality without forcing the customer to reload.
        const unsubscribe = subscribeToOrdersChanged(() => {
            dispatch(fetchBids(uniqueNumber));
        });

        return () => {
            document.removeEventListener('visibilitychange', refetchIfVisible);
            window.removeEventListener('focus', refetchIfVisible);
            unsubscribe();
        };
    }, [dispatch, uniqueNumber]);

    useEffect(() => {
        if (error) console.error('[bidding] failed to load bids', error);
    }, [error]);

    return { bids: list, order, mercure, loading };
}
